import type {
  GetCurrentUserWorkspaceMembershipsFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { PickupOpportunityFn } from '@/features/jobs'
import { collection } from '@/infra/collection'
import { once } from 'lodash-es'
import { computed, runInAction } from 'mobx'
import {
  GetOpportunityRequest,
  ListOpportunitiesRequest,
  type Opportunity as OpportunityProto,
  ListQualifiedProvidersRequest,
  ApplyToOpportunityRequest,
  GetCandidaciesRequest,
  WithdrawApplicationRequest,
} from '@taxfyle/api-internal/internal/opportunity_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { merge, NEVER, Subject, concatMap, take, type Observable } from 'rxjs'
import { filter, share, switchMap } from 'rxjs/operators'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { filterNonNil, shareLatest } from '@/utils/rx-util'
import { type OpportunitiesApi } from '@/features/opportunities/api/opportunities-api'
import { Opportunity, type OpportunityProps } from './Opportunity'
import {
  mapOpportunityConsultationProtoToConsultation,
  mapOpportunityInfoLineProtoToInfoLine,
  mapOpportunityLegendProtoToLegend,
  mapOpportunityScopeItemProtoToScopeItem,
  mapRoutingModeProtoToRoutingMode,
} from './opportunity-mappers'
import {
  maybeDecimalToNumber,
  onRpcNotFoundReturnNull,
  timestampToDate,
} from '@/utils/grpc-util'
import { type OpportunityEvent } from './opportunity-events'
import TypeCase = RealtimeEvent.TypeCase
import { type Member } from '@/features/iam/members/Member'

/**
 * Opportunity store.
 */
export class OpportunityStore {
  /**
   * Events synthesized locally. Used to immediately emit an `OpportunityRemoved` event
   * after picking up the job. If the real event comes in later, the mapping handler
   * will ignore it because the Op has already been removed from the local collection.
   *
   * @private
   */
  private readonly _synthesizedEvents = new Subject<OpportunityEvent>()

  private readonly _opportunityEvents$

  /**
   * Opportunities collection.
   */
  readonly opportunities = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Opportunity(),
  })

  /**
   * Observable that, when first subscribed to, probes whether we can view opportunities.
   */
  readonly canViewOpportunities$

  /**
   * Opportunity events.
   */
  readonly events$: Observable<OpportunityEvent>

  constructor(
    private readonly _api: OpportunitiesApi,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _currentMember$: Observable<Member>,
    private readonly _pickupDelegate: PickupOpportunityFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
  ) {
    this._opportunityEvents$ = this._api.realtime$.pipe(
      filter((event) => {
        switch (event.getTypeCase()) {
          case TypeCase.OPPORTUNITY_UPDATED:
          case TypeCase.OPPORTUNITY_REMOVED:
            return true
        }

        return false
      }),
      share(),
    )

    this.canViewOpportunities$ = this._currentMember$
      .pipe(
        take(1),
        switchMap(() => this.probeCanViewOpportunities()),
      )
      .pipe(shareLatest())

    this.events$ = merge(
      this._synthesizedEvents,
      this.canViewOpportunities$.pipe(
        // Only connect to the realtime stream if we are allowed to view ops.
        switchMap((allowed) => (allowed ? this._opportunityEvents$ : NEVER)),
        // An event that fails to map is dropped, so later events still apply.
        concatMap((e) =>
          this._mapOpportunityRealtimeEvent(e).catch(() => null),
        ),
        filterNonNil(),
      ),
    ).pipe(share())

    this.events$.subscribe()
  }

  /**
   * Lists a page of the public user IDs of the pros qualified to work on the job.
   *
   * @param jobId
   * @param query
   */
  async listQualifiedProviders(
    jobId: Id,
    query: CursorPageQuery,
  ): Promise<CursorPaged<Id>> {
    const request = new ListQualifiedProvidersRequest()
      .setId(Id.assert(jobId))
      .setPageSize(query.limit)
      .setPageToken(query.after ?? '')

    const response =
      await this._api.opportunities.listQualifiedProviders(request)
    const next = response.getNextPageToken()

    return {
      data: response
        .getQualifiedProvidersList()
        .map((provider) => Id.assert(provider.getUserPublicId())),
      next: next === '' ? null : next,
    }
  }

  /**
   * The opportunity count, filtered to only include opportunities from
   * workspaces where the user is an active member.
   */
  @computed
  get opportunityCount() {
    const memberWorkspaceIds = new Set(
      this._getCurrentUserWorkspaceMemberships().map((m) => m.workspace.id),
    )
    return this.opportunities.filter((op) =>
      memberWorkspaceIds.has(op.workspace.id),
    ).length
  }

  /**
   * Lists opportunities.
   *
   * @param query
   * @param signal
   */
  async listOpportunities(
    query: ListOpportunitiesQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Opportunity>> {
    const req = new ListOpportunitiesRequest()
    if (query.limit) {
      req.setPageSize(query.limit)
    }
    if (query.orderBy) {
      req.setOrderBy(createOrderByField(query.orderBy))
    }
    if (query.after) {
      req.setPageToken(query.after)
    }
    if (query.workspaceId) {
      req.setWorkspaceId(query.workspaceId)
    }
    if (query.searchText) {
      req.setSearchText(query.searchText)
    }

    const res = await this._api.opportunities.listOpportunities(req)
    signal?.throwIfAborted()

    const result = {
      data: this.opportunities.set(
        res
          .getOpportunitiesList()
          .map((op) => this._mapOpportunityProtoToProps(op.toObject())),
      ),
      next: res.getNextPageToken(),
    }

    const candidacies = await this.fetchCandidacies(
      result.data.map((x) => Id.assert(x.id)),
    )
    signal?.throwIfAborted()
    candidacies.forEach((c) =>
      this.opportunities.get(c)?.markUserAsCandidate(true),
    )

    return result
  }

  /**
   * Fetches candidacy information for the given page of Opportunity IDs.
   */
  async fetchCandidacies(ids: string[]) {
    return this._api.opportunities
      .getCandidacies(new GetCandidaciesRequest().setIdsList(ids))
      .then((res) => res.getCandidaciesList().map((x) => x.getId()))
  }

  /**
   * Fetches an opportunity, or `null` when it no longer exists.
   *
   * @param id
   * @param signal
   */
  async fetchOpportunity(
    id: Id,
    signal?: AbortSignal,
  ): Promise<Opportunity | null> {
    const response = await this._api.opportunities
      .getOpportunity(new GetOpportunityRequest().setId(Id.assert(id)))
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.opportunities.set(
      this._mapOpportunityProtoToProps(response.toObject()),
    )
  }

  /**
   * Picks up the opportunity.
   *
   * @param opportunity
   */
  async pickup(opportunity: Opportunity) {
    // Mark it as being picked up so in case we get the job,
    // we won't trigger the Vanished UI.
    // If it fails, undo it.
    const undo = opportunity.markAsPickingUp()
    try {
      const result = await this._pickupDelegate(opportunity.id)
      if (result !== 'Success') {
        undo()
      }

      if (this.opportunities.items.includes(opportunity)) {
        // The opportunity is still in the collection, that means
        // we haven't gotten the realtime event that we picked up the job yet.
        // We can optimistically synthesize one and emit it here so the UI
        // updates immediately, and the subsequent event that comes in later
        // will be safely ignored. This ensures that if our realtime connection
        // is down, the UI still updates when we pick up jobs.
        this.opportunities.remove(opportunity)
        this._synthesizedEvents.next({
          opportunity,
          type: 'OpportunityRemoved',
        })
      }

      return result
    } catch (err) {
      undo()
      throw err
    }
  }

  /**
   * Applies for the opportunity.
   */
  async applyForOpportunity(opportunity: Opportunity) {
    const id = Id.assert(opportunity.id)

    await this._api.opportunities.applyToOpportunity(
      new ApplyToOpportunityRequest().setId(id),
    )

    opportunity.markUserAsCandidate(true)
  }

  /**
   * Withdraws the application from the opportunity.
   */
  async withdrawApplication(opportunity: Opportunity) {
    const id = Id.assert(opportunity.id)

    await this._api.opportunities.withdrawApplication(
      new WithdrawApplicationRequest().setId(id),
    )

    opportunity.markUserAsCandidate(false)
  }

  /**
   * Probes whether we can view opportunities.
   */
  readonly probeCanViewOpportunities = once(async () => {
    try {
      await this._api.opportunities.listOpportunities(
        new ListOpportunitiesRequest().setPageSize(0),
      )
      return true
    } catch {
      return false
    }
  })

  private _mapOpportunityProtoToProps(
    proto: OpportunityProto.AsObject,
  ): Partial<OpportunityProps> {
    const workspace = this._primeWorkspace({ id: proto.workspace?.id })
    return {
      id: proto.id,
      name: proto.name,
      description: proto.description,
      scopeItems: proto.jobScopeItemsList.map(
        mapOpportunityScopeItemProtoToScopeItem,
      ),
      infoLines: proto.infoLinesList.map(mapOpportunityInfoLineProtoToInfoLine),
      legend: proto.legend
        ? mapOpportunityLegendProtoToLegend(proto.legend)
        : undefined,
      providerCutAmount: maybeDecimalToNumber(proto.providerCutAmount),
      dateDeadline: timestampToDate(proto.dateDeadline),
      dateCreated: timestampToDate(proto.dateCreated),
      dateModified: timestampToDate(proto.dateModified),
      dateTransmitted: timestampToDate(proto.dateTransmitted),
      dateScheduledDispatch:
        timestampToDate(proto.dateScheduledDispatch) ?? null,
      workspace,
      clientUser: proto.client
        ? this._primeUser({
            id: proto.client.userId,
            publicId: proto.client.userPublicId,
            givenName: proto.client.givenName?.value,
            familyName: proto.client.familyName?.value,
            picture: proto.client.picture?.value,
          })
        : undefined,
      consultation: proto.consultation
        ? mapOpportunityConsultationProtoToConsultation(proto.consultation)
        : null,
      routingMode: proto.routingMode
        ? mapRoutingModeProtoToRoutingMode(proto.routingMode)
        : undefined,
    }
  }

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   * @private
   */
  private async _mapOpportunityRealtimeEvent(
    e: RealtimeEvent,
  ): Promise<OpportunityEvent | null> {
    switch (e.getTypeCase()) {
      case TypeCase.OPPORTUNITY_UPDATED: {
        const updatedOp = e.getOpportunityUpdated()!
        const opId = updatedOp.getId()

        const isCandidate = await this.fetchCandidacies(opId ? [opId] : [])
          .then((candidacies) => candidacies.length === 1)
          // Throwing would end the event stream; keep the candidacy we know.
          .catch(() => Boolean(this.opportunities.get(opId)?.isCandidate))

        return runInAction(() => {
          const proto = updatedOp.toObject()
          // Whether (based on local data) this is considered the "became available" update.
          const becameAvailable = !this.opportunities.get(proto.id)
          const opportunity = this.opportunities.set(
            this._mapOpportunityProtoToProps(proto),
          )
          opportunity.markUserAsCandidate(isCandidate)

          return {
            type: 'OpportunityUpdated',
            opportunity,
            becameAvailable,
          }
        })
      }
      case TypeCase.OPPORTUNITY_REMOVED: {
        const payload = e.getOpportunityRemoved()!
        const op = this.opportunities.get(payload.getId())
        if (!op) {
          // We either never saw the opportunity or we already synthesized a
          // Removed event.
          return null
        }
        // We got the event in realtime and we haven't removed the Op ourselves,
        // this means that we didn't pick it up.
        this.opportunities.remove(op)
        return { type: 'OpportunityRemoved', opportunity: op }
      }
    }
    return null
  }
}

/**
 * Fields that the Opportunities can be sorted by.
 */
export type OpportunitySortingField =
  | 'Created'
  | 'Deadline'
  | 'Modified'
  | 'Transmitted'
  | 'CustomerFirstName'
  | 'Name'
  | 'Description'
  | 'Score'

/**
 * Lists opportunities.
 */
export type ListOpportunitiesFn = (
  query: ListOpportunitiesQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<Opportunity>>

/**
 * Query for listing opportunities.
 */
export interface ListOpportunitiesQuery extends CursorPageQuery {
  workspaceId?: string
  searchText?: string
  orderBy?: Partial<Record<OpportunitySortingField, 'asc' | 'desc'>>
}

/**
 * Creates an order_by field.
 *
 * @param orderBy
 */
function createOrderByField(orderBy: ListOpportunitiesQuery['orderBy']) {
  const buf: string[] = []
  if (!orderBy) {
    return ''
  }

  for (const [field, order] of Object.entries(orderBy)) {
    buf.push(
      `${mapOpportunitySortingFieldToWire(field as OpportunitySortingField)} ${order}`,
    )
  }
  return buf.join(',')
}

function mapOpportunitySortingFieldToWire(field: OpportunitySortingField) {
  switch (field) {
    case 'Created':
      return 'created'
    case 'Deadline':
      return 'deadline'
    case 'Modified':
      return 'modified'
    case 'Transmitted':
      return 'transmitted'
    case 'CustomerFirstName':
      return 'customer_first_name'
    case 'Name':
      return 'name'
    case 'Description':
      return 'description'
    case 'Score':
      return 'score'
  }
}
